import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { analytics } from '../../services/analytics';
import SEOPageLayout from '../../components/SEOPageLayout';

const CheckIcon = (props) => (
  <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GoodBetterBestPricing = () => {
  useEffect(() => {
    analytics.track('Feature Page View', { feature: 'Good-Better-Best Pricing' });
  }, []);

  return (
    <SEOPageLayout
      title="Good Better Best Pricing Software - Increase Average Ticket Size | QuoteTier"
      description="Stop leaving money on the table. Our Good/Better/Best pricing software helps service businesses present options that upsell themselves."
      canonical="/features/good-better-best-pricing-software"
      ogTitle="The Easiest Way to Offer Tiered Pricing"
      ogDescription="Present 3 clear options to every customer. Watch your average ticket size grow by 30% or more with interactive tiered quotes."
    >
      {/* Hero Section */}
      <section className="py-32 md:py-40 lg:py-48">
        <div className="container max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-instrument text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tight leading-tight mb-6">
              The <span className="text-primary font-bold">Psychology of Choice</span> in Your Pocket
            </h1>
            <p className="font-jetbrains text-xl md:text-2xl text-gray-600 tracking-tight leading-relaxed mb-8">
              Stop offering a single price. Use Good/Better/Best pricing to let customers choose the value that fits their budget.
            </p>
            <div className="font-instrument mt-12 flex flex-col sm:flex-row gap-6 justify-center items-center mb-6">
              <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'gbb-hero', action: 'signup' })}>
                <Button className="px-8 py-4 text-xl font-semibold bg-black text-white hover:bg-gray-800 rounded-lg shadow-lg">
                  Start Free — 3 Quotes/Mo
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why It Works */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-4">Why Tiered Pricing Wins</h2>
            <p className="font-jetbrains text-lg text-gray-600">It's not just about price; it's about control.</p>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            <Card className="border border-black">
              <CardHeader>
                <CardTitle className="font-instrument text-xl">Eliminates "Sticker Shock"</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-jetbrains text-sm text-gray-600">
                  When you offer only one price, the question is "Should I buy this?". When you offer three, the question becomes "Which one should I buy?".
                </p>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader>
                <CardTitle className="font-instrument text-xl">Anchoring Effect</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-jetbrains text-sm text-gray-600">
                  A high-priced "Best" option makes the "Better" option look like a bargain, nudging customers away from the cheapest "Good" option.
                </p>
              </CardContent>
            </Card>
            <Card className="border border-black">
              <CardHeader>
                <CardTitle className="font-instrument text-xl">Captures All Budgets</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-jetbrains text-sm text-gray-600">
                  Don't lose customers who want the cheapest option, and don't limit customers who want the best. Serve everyone with one quote.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Feature Details */}
      <section className="py-16 md:py-24">
        <div className="container max-w-6xl mx-auto px-4">
          <div className="grid gap-12 md:grid-cols-2 items-center">
            <div>
              <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">Built for Service Pros</h2>
              <ul className="space-y-4 font-jetbrains text-lg text-gray-700">
                <li className="flex items-start gap-3">
                  <CheckIcon className="h-6 w-6 text-primary mt-1" />
                  <span><strong>Visual Comparison:</strong> Side-by-side columns make differences obvious.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckIcon className="h-6 w-6 text-primary mt-1" />
                  <span><strong>Interactive Upgrades:</strong> Clients can toggle optional add-ons themselves.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckIcon className="h-6 w-6 text-primary mt-1" />
                  <span><strong>Instant Sign-Off:</strong> They choose a package and sign immediately on their phone.</span>
                </li>
              </ul>
            </div>
            <div className="bg-gray-100 p-8 rounded-2xl border border-gray-200">
               {/* Placeholder for a visual representation of the UI */}
               <div className="space-y-4">
                 <div className="flex gap-4">
                   <div className="flex-1 bg-white p-4 rounded shadow-sm border border-gray-200 opacity-50">
                     <div className="h-4 w-20 bg-gray-200 mb-2 rounded"></div>
                     <div className="h-8 w-16 bg-gray-300 rounded"></div>
                   </div>
                   <div className="flex-1 bg-white p-4 rounded shadow-md border-2 border-black transform scale-105">
                     <div className="h-4 w-20 bg-gray-200 mb-2 rounded"></div>
                     <div className="h-8 w-16 bg-black rounded"></div>
                   </div>
                   <div className="flex-1 bg-white p-4 rounded shadow-sm border border-gray-200 opacity-50">
                     <div className="h-4 w-20 bg-gray-200 mb-2 rounded"></div>
                     <div className="h-8 w-16 bg-gray-300 rounded"></div>
                   </div>
                 </div>
                 <p className="text-center text-sm text-gray-500 font-jetbrains">Simple, clean, effective.</p>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-black text-white">
        <div className="container max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-instrument text-3xl md:text-4xl font-bold mb-6">
            Ready to Upgrade Your Pricing Strategy?
          </h2>
          <Link to="/signup" onClick={() => analytics.track('CTA Clicked', { location: 'gbb-footer', action: 'signup' })}>
            <Button className="px-10 py-4 font-instrument text-xl font-semibold bg-white text-black hover:bg-gray-200 rounded-lg shadow-lg">
              Create Your First Tiered Quote
            </Button>
          </Link>
        </div>
      </section>
    </SEOPageLayout>
  );
};

export default GoodBetterBestPricing;
